import React, { useState } from 'react';
import { useBlog } from '../../contexts/BlogContext';
import { Plus, Edit2, Trash2, Save, X } from 'lucide-react';

const Categories = () => {
  const { categories, addCategory, updateCategory, deleteCategory } = useBlog();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newCategory, setNewCategory] = useState('');
  const [editValue, setEditValue] = useState('');

  const handleAdd = () => {
    if (newCategory.trim()) {
      addCategory(newCategory.trim());
      setNewCategory('');
      setIsAdding(false);
    }
  };

  const handleUpdate = (id) => {
    if (editValue.trim()) {
      updateCategory(id, editValue.trim());
      setEditingId(null);
      setEditValue('');
    }
  };

  const handleDelete = (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}" category?`)) {
      deleteCategory(id);
    }
  };

  const startEdit = (id, name) => {
    setEditingId(id);
    setEditValue(name);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-primary mb-2">Manage Categories</h1>
          <p className="text-gray-600">Add, edit, or delete blog post categories</p>
        </div>

        {/* Add Category Button */}
        <div className="mb-6">
          {!isAdding ? (
            <button
              onClick={() => setIsAdding(true)}
              className="bg-primary hover:bg-primary-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors font-semibold"
            >
              <Plus className="w-5 h-5" />
              Add New Category
            </button>
          ) : (
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
                  placeholder="Enter category name..."
                  className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                  autoFocus
                />
                <button
                  onClick={handleAdd}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <Save className="w-4 h-4" />
                  Save
                </button>
                <button
                  onClick={() => {
                    setIsAdding(false);
                    setNewCategory('');
                  }}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Categories List */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-primary to-primary-700">
            <h2 className="text-xl font-bold text-white">All Categories ({categories?.length || 0})</h2>
          </div>

          <div className="divide-y divide-gray-200">
            {!categories || categories.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">
                No categories yet. Add your first category!
              </div>
            ) : (
              categories.map((category) => (
                <div key={category.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  {editingId === category.id ? (
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleUpdate(category.id)}
                        className="flex-1 px-4 py-2 border-2 border-primary rounded-lg focus:outline-none"
                        autoFocus
                      />
                      <button
                        onClick={() => handleUpdate(category.id)}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                      >
                        <Save className="w-4 h-4" />
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setEditingId(null);
                          setEditValue('');
                        }}
                        className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                      >
                        <X className="w-4 h-4" />
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{category.name}</h3>
                        <p className="text-sm text-gray-500">
                          {category.count || 0} posts in this category
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => startEdit(category.id, category.name)}
                          className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors"
                          title="Edit Category"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(category.id, category.name)}
                          className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded transition-colors"
                          title="Delete Category"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-blue-50 rounded-xl p-6 border border-blue-200">
          <h3 className="text-lg font-bold text-blue-900 mb-3">📝 How to Use Categories</h3>
          <ul className="space-y-2 text-blue-800">
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">•</span>
              <span><strong>Add Category:</strong> Click "Add New Category" button above</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">•</span>
              <span><strong>Edit Category:</strong> Click the blue edit icon to rename</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">•</span>
              <span><strong>Delete Category:</strong> Click the red trash icon (with confirmation)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">•</span>
              <span><strong>Use in Posts:</strong> Select category from dropdown when creating/editing posts</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Categories;
